import { setAuthTokenCookie } from '#shared/utils/authTokenCookie';

/**
 * Has the server re-issue the session cookie that nuxt-auth writes by script, so that WebKit does not delete it: see
 * `setAuthTokenCookie`.
 *
 * - Page render: the response carries the cookie again. Runs after nuxt-auth's own plugin, so a token rejected by the
 *   session check is already gone and nothing is re-issued. A rejection later in the render (route middleware) deletes
 *   the cookie after this, and the last `Set-Cookie` for the same cookie is the one that reaches the browser.
 * - New token in the browser (login, signup, email change): the app may not render a page on the server again for as
 *   long as the tab stays open, so a request to the server route re-issues it straight away.
 */
export default defineNuxtPlugin({
    name: 'authCookieRefresh',
    enforce: 'post',
    setup() {
        const { rawToken } = useAuthState();

        if (import.meta.server) {
            const event = useRequestEvent();

            // The locale redirect on `/` answers before the app has booted: there is no response left to add a header to.
            if (event && !event.node.res.headersSent && rawToken.value) setAuthTokenCookie(event, rawToken.value, useRuntimeConfig().public.auth);

            return;
        }

        watch(
            rawToken,
            token => {
                // A failed request leaves the script-written cookie until the next page render re-issues it
                if (token) $fetch('/api/auth-token-cookie', { method: 'POST' }).catch(() => undefined);
            },
            // By then nuxt-auth's own watcher has written the new token to `document.cookie`, so the request carries it
            { flush: 'post' }
        );
    },
});
